import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useAtom } from "jotai";
import { themeAtom, isMobileAtom, listsAtom } from "../../../-Atoms";
import { motion, AnimatePresence } from "framer-motion";
import {
  db,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  onSnapshot,
  functions,
  httpsCallable,
} from "../../../firebaseConfig";
import StoryboardOperatorChecklist from "./StoryboardOperatorChecklist";
import ListSelector from "./JobTab/ListSelector";
import EmailModalV2 from "../../../EmailModalV2/EmailModalV2";
import { getDropboxUrlWithRetry } from "../../../DropboxFunctions";
import getJobName from "../../../GlobalFunctions/getJobName";
import {
  generateEmailContent,
  updateTemplateWithSubject,
  EMAIL_CONFIGS,
} from "../../../GlobalFunctions/EmailContentGenerator";

// Legacy functions removed - now using unified EmailContentGenerator

// Helper function to generate email content with override config
const generateEmailContentWithOverride = async (
  jobData,
  clientData,
  userSelections,
  configKey,
  config
) => {
  // Load the email template
  const template = await fetch(`/EmailTemplates/${config.template}`).then((r) =>
    r.text()
  );

  // Generate the download links using the config's settings
  const isProductJob = jobData?.jobTypeFull?.includes("-P");
  const basePath = `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${
    jobData.jobTypeShort
  }${jobData.multipleDays ? `/${jobData.multipleDays}` : ``}`;

  // Create folders in background based on config (like original generateLinks function)
  const folderCreationPromises = [];

  if (config.folderPaths.base) {
    folderCreationPromises.push(getDropboxUrlWithRetry(basePath));
  }
  if (config.folderPaths.storyboards) {
    folderCreationPromises.push(
      getDropboxUrlWithRetry(`${basePath}/Storyboards`)
    );
  }
  if (config.folderPaths.storyboardSelects) {
    folderCreationPromises.push(
      getDropboxUrlWithRetry(`${basePath}/Storyboard Selects`)
    );
  }
  if (config.folderPaths.quickproofs) {
    folderCreationPromises.push(
      getDropboxUrlWithRetry(`${basePath}/Quickproofs`)
    );
  }

  // Start folder creation in background (don't await) - matches original behavior
  folderCreationPromises.forEach((promise) => promise.catch(console.error));

  // Create appropriate link based on config
  let links;
  if (!config.useSupplyPortal) {
    // For direct Dropbox URLs (supply emails), wait for and return the actual URL
    const dropboxUrl = await getDropboxUrlWithRetry(basePath);
    if (dropboxUrl) {
      links = `<a href="${dropboxUrl}" contentEditable="false" target="_blank" style="display: inline-block; padding: 12px 24px; background-color: #000000; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: bold; margin: 8px;">${config.linkLabel}</a>`;
    } else {
      throw new Error("Unable to generate Dropbox URL for supply email");
    }
  } else {
    // For supply portal URLs
    const supplyUrl =
      process.env.NODE_ENV === "production"
        ? `https://supply.imagesthatsell.com.au/${jobData.id}`
        : `https://localhost:3000/supply/${jobData.id}`;

    links = `<a href="${supplyUrl}" contentEditable="false" target="_blank" style="display: inline-block; padding: 12px 24px; background-color: #000000; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: bold; margin: 8px;">${config.linkLabel}</a>`;
  }

  // Prepare email data using the override config
  const emailData = {
    subject: getJobName(jobData),
    header: config.header,
    supplyText: config.supplyText,
    emailBody: config.emailBody,
    links: links,
    isInternalOnly: configKey === "INTERNAL_ONLY",
  };

  // Process the template with the data
  let processed = template;
  if (emailData.isInternalOnly) {
    processed = processed.replace(/{{{header}}}/g, "");
    processed = processed.replace(
      /{{{supply_text}}}/g,
      emailData.supplyText || ""
    );
  } else {
    processed = processed.replace(/{{{header}}}/g, emailData.header || "");
    processed = processed.replace(
      /{{{supply_text}}}/g,
      emailData.supplyText || ""
    );
  }
  processed = processed.replace(/{{{subject}}}/g, emailData.subject || "");
  processed = processed.replace(/{{{links}}}/g, emailData.links || "");
  processed = processed.replace(/{{{email_body}}}/g, emailData.emailBody || "");

  return {
    template: processed,
    subject: emailData.subject,
    links: links,
    emailBody: emailData.emailBody,
    baseTemplate: template,
    emailData: emailData,
  };
};

const StoryboardDetailsModal = ({ open, onClose, jobData, clientData }) => {
  const [theme] = useAtom(themeAtom);
  const [isMobile] = useAtom(isMobileAtom);
  const [lists] = useAtom(listsAtom);

  const sendEmail = httpsCallable(functions, "sendEmail");

  const noRetouching = jobData?.noRetouching || false;
  const paymentTerms = clientData?.paymentTerms || "";

  const isProductJob = jobData?.jobTypeFull?.includes("-P");

  const [selectsStatus, setSelectsStatus] = useState(true);
  const [checklistComplete, setChecklistComplete] = useState(false);
  const [imagesProcessedComplete, setImagesProcessedComplete] = useState(false);
  const [imagesProcessedText, setImagesProcessedText] = useState(
    "Fill In Images Processed"
  );
  const [idList, setIdList] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retouchingRequired, setRetouchingRequired] = useState(!noRetouching);

  const [clientEmailMode, setClientEmailMode] = useState("Internal Only");

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailToRecipients, setEmailToRecipients] = useState([]);
  const [emailCcRecipients, setEmailCcRecipients] = useState([]);

  const [baseTemplate, setBaseTemplate] = useState("");
  const [emailLinks, setEmailLinks] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailData, setEmailData] = useState(null); // Store email data for dynamic updates

  const [templateOverrideMenuAnchor, setTemplateOverrideMenuAnchor] =
    useState(null);

  const [uploadStates] = useState({
    storyboards: {
      isUploading: false,
      progress: null,
      error: "",
      success: "",
      abortController: null,
    },
    storyboardSelects: {
      isUploading: false,
      progress: null,
      error: "",
      success: "",
      abortController: null,
    },
    quickproofs: {
      isUploading: false,
      progress: null,
      error: "",
      success: "",
      abortController: null,
    },
  });

  // Template override options - map to EMAIL_CONFIG keys
  const allEmailTemplateOptions = [
    // Product job options
    {
      configKey: "PRODUCT_STORYBOARD_CONFIRMED",
      displayName: "Product - Selects Confirmed",
      description: "Storyboards ready for confirmed product selects",
      jobTypes: ["product"],
    },
    {
      configKey: "PRODUCT_STORYBOARD_NOT_CONFIRMED",
      displayName: "Product - Selects Not Confirmed",
      description: "Storyboards ready, client needs to edit selects",
      jobTypes: ["product"],
    },
    {
      configKey: "INTERNAL_ONLY",
      displayName: "Internal Only",
      description: "Internal storyboard access only",
      jobTypes: ["product", "life"],
    },
    // Life job options
    {
      configKey: "LIFE_NO_RETOUCHING_COD",
      displayName: "No Retouching - COD",
      description: "Files ready, COD payment required for supply",
      jobTypes: ["life"],
    },
    {
      configKey: "LIFE_NO_RETOUCHING_SUPPLY",
      displayName: "No Retouching - Supply",
      description: "Files ready for immediate download",
      jobTypes: ["life"],
    },
    {
      configKey: "LIFE_RETOUCHING_CONFIRMED",
      displayName: "Retouching - Selects Confirmed",
      description:
        "Storyboards ready, management will confirm selects via email",
      jobTypes: ["life"],
    },
    {
      configKey: "LIFE_RETOUCHING_NOT_CONFIRMED",
      displayName: "Retouching - Selects Not Confirmed",
      description: "Storyboards ready, client needs to confirm selects",
      jobTypes: ["life"],
    },
  ];

  const emailTemplateOptions = allEmailTemplateOptions.filter((template) => {
    const jobType = isProductJob ? "product" : "life";
    return template.jobTypes.includes(jobType);
  });

  const [invoices, setInvoices] = useState([]);
  const [invoiceStatusSummary, setInvoiceStatusSummary] = useState([
    "No Invoice",
  ]);

  const getPaymentStatus = (invoice) => {
    if (invoice.Status === "VOIDED") {
      return "VOIDED";
    } else if (invoice.Status === "DRAFT") {
      return "DRAFT";
    } else if (!invoice.SentToContact) {
      return "DRAFT";
    } else if (invoice.AmountDue <= 0 && invoice.Total > 0) {
      return "Paid";
    } else if (invoice.AmountPaid > 0 && invoice.AmountDue > 0) {
      return "Partially Paid";
    } else {
      return "Unpaid";
    }
  };

  const getClientEmails = () => {
    if (!clientData?.contacts) return [];
    return clientData.contacts
      .filter((contact) => contact.email)
      .map((contact) => ({ email: contact.email, name: contact.name || "" }));
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "#4caf50";
      case "Partially Paid":
        return "#ff9800";
      case "Unpaid":
        return "#f44336";
      case "VOIDED":
        return "#f44336";
      case "DRAFT":
        return "#2196f3";
      case "No Invoice":
        return "#757575";
      default:
        return theme === "dark" ? "#f5f5f5" : "#757575";
    }
  };

  const setupEmailRecipients = () => {
    let toEmails, ccEmails;

    if (isProductJob) {
      if (clientEmailMode === "Internal Only") {
        // toEmails = [{ email: "alex@imagesthatsell.com.au" }];
        toEmails = [{ email: "adam@imagesthatsell.com.au" }];
        ccEmails = [];

        if (
          jobData?.jobTypeFull?.includes("-LC") ||
          jobData?.jobTypeFull?.includes("-PC")
        ) {
          ccEmails.push({ email: "bookings@imagesthatsell.com.au" });
        }
      } else {
        toEmails = getClientEmails();
        ccEmails =
          jobData?.client !== "Morrissey Media"
            ? [{ email: "accounts@imagesthatsell.com.au" }]
            : [];
      }
    } else {
      toEmails = getClientEmails();
      ccEmails =
        jobData?.client !== "Morrissey Media"
          ? [{ email: "accounts@imagesthatsell.com.au" }]
          : [];
    }

    return { toEmails, ccEmails };
  };

  useEffect(() => {
    if (open) {
      setSelectsStatus(true);
      setChecklistComplete(false);
      setError("");
      setRetouchingRequired(!noRetouching);
    }
  }, [open, noRetouching]);

  // Monitor images processed numbers validation (similar to old EmailSelectorDialog)
  useEffect(() => {
    if (jobData?.imagesProcessedNumbers) {
      setImagesProcessedComplete(true);
      setImagesProcessedText("Images Processed ✓");
    } else {
      setImagesProcessedComplete(false);
      setImagesProcessedText("Fill In Images Processed");
    }
  }, [jobData?.imagesProcessedNumbers]);

  useEffect(() => {
    if (!jobData?.jobNumber) {
      return;
    }

    const q = query(
      collection(db, "xero-invoices"),
      where(
        "containsJobNumbers",
        "array-contains",
        jobData.jobNumber.toString()
      )
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const invoiceData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInvoices(invoiceData);
      },
      (error) => {
        console.error("Error fetching invoices:", error);
      }
    );

    return () => unsubscribe();
  }, [jobData]);

  useEffect(() => {
    if (invoices.length === 0) {
      setInvoiceStatusSummary(["No Invoice"]);
      return;
    }

    const statusCounts = invoices.reduce((acc, invoice) => {
      const status = getPaymentStatus(invoice);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const result = Object.entries(statusCounts).map(([status, count]) =>
      count > 1 ? `${count} ${status}` : status
    );

    setInvoiceStatusSummary(result);
  }, [invoices]);

  useEffect(() => {
    let tmpId = "";
    lists?.forEach((list) => {
      if (list.items?.indexOf(jobData.id) !== -1) {
        if (!tmpId) {
          tmpId = list.id;
          setIdList(list.id);
        } else {
          alert(
            "List Conflict: Job is in 2 Lists, this shouldn't be possible. Please screenshot this (including the job number) and send it to Alex. The first list to be found is what will be used."
          );
        }
      }
    });

    return () => {
      setIdList("");
    };
  }, [lists, jobData]);

  // Helper function to add permanent labels to job (from original EmailModal.js)
  const addLabelToCardFB = async (jobData, labelId) => {
    if (!jobData?.id) return;

    const updatedJob = { ...jobData };

    if (updatedJob.idLabelsPermanent) {
      if (!updatedJob.idLabelsPermanent.includes(labelId)) {
        updatedJob.idLabelsPermanent.push(labelId);
      }
    } else {
      updatedJob.idLabelsPermanent = [labelId];
    }

    const noDupes = [...new Set(updatedJob.idLabelsPermanent)];
    updatedJob.idLabelsPermanent = noDupes;

    await updateDoc(doc(db, "JobData", jobData.id), {
      idLabelsPermanent: updatedJob.idLabelsPermanent,
    });
  };

  const handleRetouchingRequiredChange = async (_, newValue) => {
    if (newValue === null) return;

    setRetouchingRequired(newValue);

    try {
      await updateDoc(doc(db, "JobData", jobData.id), {
        noRetouching: !newValue,
      });
    } catch (error) {
      console.error("Error updating retouching status:", error);

      setRetouchingRequired(!newValue);
    }
  };

  const canProceed = () => {
    if (isProductJob) {
      return selectsStatus !== null;
    } else if (!retouchingRequired) {
      return true;
    } else {
      if (selectsStatus === null) {
        return false;
      }
      // For non-product jobs requiring retouching, also validate images processed
      return checklistComplete && imagesProcessedComplete;
    }
  };

  const handleContinue = async (overrideConfigKey = null) => {
    try {
      setLoading(true);
      setError("");

      const { toEmails, ccEmails } = setupEmailRecipients();

      // Prepare user selections for the unified email generator
      const userSelections = {
        selectsStatus: selectsStatus,
        retouchingRequired: retouchingRequired,
        isInternalOnly: isProductJob
          ? clientEmailMode === "Internal Only"
          : false,
      };

      let emailResult;

      if (overrideConfigKey) {
        // For template overrides, manually generate email with specific config
        const config = EMAIL_CONFIGS[overrideConfigKey];
        if (!config) {
          throw new Error(`Unknown email configuration: ${overrideConfigKey}`);
        }

        // Generate email content by directly using the specified config
        emailResult = await generateEmailContentWithOverride(
          jobData,
          clientData,
          userSelections,
          overrideConfigKey,
          config
        );
      } else {
        // Use the normal unified email content generator
        emailResult = await generateEmailContent(
          jobData,
          clientData,
          userSelections
        );
      }

      // Set up email modal with generated content
      setEmailTemplate(emailResult.template);
      setEmailSubject(emailResult.subject);
      setEmailToRecipients(toEmails);
      setEmailCcRecipients(ccEmails);

      // Store template processing state for dynamic updates
      setBaseTemplate(emailResult.baseTemplate);
      setEmailLinks(emailResult.links);
      setEmailBody(emailResult.emailBody);
      setEmailData(emailResult.emailData);

      // Create ClientSelects document when selects are not confirmed
      // This ensures Supply Portal will use standard folder structure for jobs requiring client selection
      const shouldCreateClientSelects = overrideConfigKey
        ? overrideConfigKey === "PRODUCT_STORYBOARD_NOT_CONFIRMED" ||
          overrideConfigKey === "LIFE_RETOUCHING_NOT_CONFIRMED"
        : selectsStatus === false;

      if (shouldCreateClientSelects) {
        try {
          const clientSelectsRef = doc(db, "ClientSelects", jobData.id);
          const clientSelectsDoc = await getDoc(clientSelectsRef);

          if (!clientSelectsDoc.exists()) {
            await setDoc(clientSelectsRef, {
              selectedImages: [],
              selectsMade: false,
              imagesProcessed: false,
            });
          }
        } catch (error) {
          console.error("Error creating ClientSelects document:", error);
          // Don't fail the email flow if ClientSelects creation fails
        }
      }

      setEmailModalOpen(true);
    } catch (err) {
      console.error("Error preparing storyboard email:", err);
      setError(err.message || "Failed to prepare email");
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateOverride = (configKey) => {
    setTemplateOverrideMenuAnchor(null);
    handleContinue(configKey);
  };

  const handleSubjectChange = (newSubject) => {
    setEmailSubject(newSubject);

    // For jobs with template processing, update the template dynamically
    if (baseTemplate && emailData) {
      const updatedTemplate = updateTemplateWithSubject(
        baseTemplate,
        emailData,
        newSubject
      );
      setEmailTemplate(updatedTemplate);
    }
  };

  const handleSend = async (emailData) => {
    try {
      // Send the original email
      const result = await sendEmail({
        emailBody: emailData.htmlContent,
        fromEmail: "production@imagesthatsell.com.au",
        toEmails: emailData.to,
        ccEmails: emailData.cc,
        bccEmails: emailData.bcc,
        replyToEmails: emailData.replyTo,
        subject: emailData.subject,
        attachments: emailData.attachments || [],
      });

      if (result.data.error) {
        throw new Error(result.data.errortext);
      } else {
        console.log("Storyboard email sent successfully");

        // Add storyboards sent permanent label to job
        await addLabelToCardFB(jobData, "c9324864-f503-4267-a539-2c5accd94f7f");

        // Send notification email to bookings (only for Life, Creative Life, and Creative Product jobs)
        if (
          jobData?.jobTypeFull?.includes("-L") ||
          jobData?.jobTypeFull?.includes("-LC") ||
          jobData?.jobTypeFull?.includes("-PC")
        ) {
          await sendBookingsNotification(emailData);
        }

        handleEmailModalClose();
      }
    } catch (error) {
      console.error("Error sending storyboard email:", error);
      throw error;
    }
  };

  const sendBookingsNotification = async (emailData) => {
    try {
      // Get the current list name
      const currentList = lists?.find((list) => list.id === idList);
      const listName = currentList?.name || "Unknown List";
      console.log(listName);

      // Calculate file breakdown
      const imageBreakdown = jobData?.imageBreakdown || [];
      let totalProcessed = 0;
      const breakdownItems = imageBreakdown
        .map((item) => {
          const quantity = parseInt(item.quantityProcessed) || 0;
          if (quantity > 0 && !item.dontAddToTotalSupplied) {
            totalProcessed += quantity;
            return `<div class="row"><span class="label">${item.description}:</span>${quantity}</div>`;
          }
          return null;
        })
        .filter(Boolean)
        .join("");

      const bookingsEmailHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light dark">
  <meta name="supported-color-schemes" content="light dark">
  <style>
    /* Dark mode */
    @media (prefers-color-scheme: dark) {
      body, .email-wrapper { background-color: #1a1a1a !important; }
      .email-content { background-color: #2d2d2d !important; border-color: #444 !important; }
      .section { background-color: #333 !important; }
      h1, h2, p, div, span { color: #ffffff !important; }
      .logo-img { filter: invert(1) !important; }
      .warning {
        background: #664d03 !important;
        color: #fff !important;
      }
      .warning-title {
        color: #ffd54f !important; /* softer amber accent in dark mode */
      }
      .button {
        background-color: #000000 !important;
        color: #ffffff !important;
      }
    }

    /* Outlook dark mode */
    [data-ogsc] body, [data-ogsc] .email-wrapper { background-color: #1a1a1a !important; }
    [data-ogsc] .email-content { background-color: #2d2d2d !important; border-color: #444 !important; }
    [data-ogsc] .section { background-color: #333 !important; }
    [data-ogsc] h1, [data-ogsc] h2, [data-ogsc] p, [data-ogsc] div, [data-ogsc] span { color: #ffffff !important; }
    [data-ogsc] .logo-img { filter: invert(1) !important; }
    [data-ogsc] .warning {
      background: #664d03 !important;
      color: #fff !important;
    }
    [data-ogsc] .warning-title {
      color: #ffd54f !important;
    }
    [data-ogsc] .button {
      background-color: #000000 !important;
      color: #ffffff !important;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      margin: 0; padding: 0;
      background-color: #f3f4f6;
    }
    .email-wrapper { width: 100%; padding: 40px 0; }
    .email-content {
      background: #ffffff;
      border-radius: 12px;
      max-width: 700px;
      margin: 0 auto;
    }
    .inner { padding: 40px; text-align: center; }
    .logo-img {
      display: block;
      margin: 0 auto 30px auto;
      width: 120px;
      outline: none;
      border: none;
    }
    h1 {
      font-size: 22px;
      font-weight: 700;
      color: #111827;
      margin-bottom: 8px;
      text-align: center;
    }
    h2.subtitle {
      font-size: 16px;
      font-weight: 500;
      color: #6b7280;
      margin-bottom: 30px;
      text-align: center;
    }
    .section {
      background: #f9fafb;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;
      text-align: left;
    }
    .row { margin: 6px 0; font-size: 14px; color: #374151; }
    .label { font-weight: 600; color: #111827; margin-right: 6px; }
    .warning {
      background: #fff3cd;
      color: #856404;
      padding: 16px;
      border-radius: 6px;
      margin-top: 12px;
      font-size: 14px;
      text-align: left;
    }
    .warning-title {
      display: block;
      font-weight: bold;
      color: #b45309; /* deep amber in light mode */
      margin-bottom: 6px;
    }
    .button {
      display: inline-block;
      padding: 12px 24px;
      background-color: #000000;
      color: #ffffff !important;
      text-decoration: none;
      border-radius: 6px;
      font-weight: bold;
      margin: 12px auto 0 auto;
    }
  </style>
</head>
<body>
  <table class="email-wrapper" role="presentation" cellspacing="0" cellpadding="0">
    <tr>
      <td align="center">
        <table class="email-content" role="presentation" cellspacing="0" cellpadding="0">
          <tr>
            <td class="inner">
              <img class="logo-img" src="data:image/png;base64,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"
 alt="ITS Logo" />

              <h1>${getJobName(jobData)}</h1>
              <h2 class="subtitle">Follow up information</h2>

              <div class="section">
                <div class="row"><span class="label">Client:</span>${
                  jobData?.client || "N/A"
                }</div>
                <div class="row"><span class="label">Job Number:</span>${
                  jobData?.jobNumber || "N/A"
                }</div>
                <div class="row"><span class="label">Job Type:</span>${
                  jobData?.jobTypeFull || "N/A"
                }</div>
              </div>

              <div class="section">
                <div class="row"><span class="label">Selects Status:</span>${
                  selectsStatus ? "Confirmed" : "Not Confirmed"
                }</div>
                <div class="row"><span class="label">Retouching Required:</span>${
                  retouchingRequired ? "Yes" : "No"
                }</div>
              </div>

              ${
                breakdownItems
                  ? `
              <div class="section">
                <div class="row" style="font-weight: 600; margin-bottom: 12px; font-size: 15px;">File Count Breakdown</div>
                ${breakdownItems}
                <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
                  <div class="row"><span class="label" style="font-size: 15px;">Total Processed:</span><strong>${totalProcessed}</strong></div>
                </div>
              </div>
              `
                  : ""
              }

              <div class="section">
                <div class="row"><span class="label">Current List:</span>${listName}</div>
                ${
                  listName === "ON HOLD 🛑"
                    ? `
                      <div class="warning">
                        <span class="warning-title">Action Required:</span>
                        Please follow up with the client then move this job to "Prep To Send" once selects are confirmed.
                        <br/><br/>
                        <div style="text-align:center;">
                          <a 
                            href="mailto:${emailData.to.join(
                              ","
                            )}?subject=${encodeURIComponent(
                        emailData.subject
                      )}" 
                            target="_blank" 
                            class="button"
                          >
                            Email Clients
                          </a>
                        </div>
                      </div>
                    `
                    : ""
                }
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`;

      // Send the bookings notification email
      const bookingsResult = await sendEmail({
        emailBody: bookingsEmailHtml,
        fromEmail: "production@imagesthatsell.com.au",
        // toEmails: ["alex@imagesthatsell.com.au"],
        toEmails: ["bookings@imagesthatsell.com.au"],
        ccEmails: [],
        bccEmails: [],
        replyToEmails: [],
        subject: `Follow up - ${getJobName(jobData)}`,
        attachments: [],
      });

      if (bookingsResult.data.error) {
        console.error(
          "Error sending bookings notification:",
          bookingsResult.data.errortext
        );
      } else {
        console.log("Bookings notification sent successfully");
      }
    } catch (error) {
      console.error("Error sending bookings notification:", error);
      // Don't throw error here to avoid breaking the main email flow
    }
    onClose();
  };

  const handleEmailModalClose = () => {
    setEmailModalOpen(false);
    onClose();
  };

  const isDark = theme === "dark";

  const themeStyles = {
    background: isDark ? "#0d0d0d" : "#f5f5f5",
    color: isDark ? "#ffffff" : "#000000",
    borderColor: isDark ? "#2c2c2e" : "#d1d1d6",
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
        staggerChildren: 0.03,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.15,
        ease: "easeInOut",
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
        ease: "easeInOut",
      },
    },
  };

  const getStatusColor = (isRetouching) => {
    return isRetouching
      ? isDark
        ? "#FF9800"
        : "#ED6C02"
      : isDark
      ? "#4CAF50"
      : "#2E7D32";
  };

  const filterButtonStyles = {
    fontWeight: "bold",
    padding: "8px 12px",
    minWidth: "120px",
    border: `1px solid ${
      isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
    } !important`,
    borderRadius: "8px !important",
    fontSize: 13,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "none",
    boxShadow: isDark
      ? "0 2px 5px rgba(0,0,0,0.2)"
      : "0 2px 5px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap",
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: themeStyles.background,
              color: themeStyles.color,
              borderRadius: isMobile ? 0 : "16px",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            pb: 1,
          }}
        >
          {isProductJob
            ? "Product Storyboard Details…"
            : "Just a few details before sending the email…"}
        </DialogTitle>

        <DialogContent sx={{ pb: 2 }}>
          <AnimatePresence>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Status fields with EventDataModalV2 styling */}
              <Box
                sx={{
                  mb: 1,
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: isDark
                    ? "rgba(35,35,35,0.7)"
                    : "rgba(235,235,235,0.9)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isDark
                    ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                    : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                  padding: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* Product Job: Client Email Mode Toggle OR Lifecycle Job: Retouching Required Toggle */}
                  <motion.div variants={itemVariants}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 500 }}
                      >
                        {isProductJob
                          ? "Email Recipients:"
                          : "Retouching Required:"}
                      </Typography>
                      <ToggleButtonGroup
                        value={
                          isProductJob ? clientEmailMode : retouchingRequired
                        }
                        exclusive
                        onChange={
                          isProductJob
                            ? (_, newValue) =>
                                newValue && setClientEmailMode(newValue)
                            : handleRetouchingRequiredChange
                        }
                        sx={{
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Tooltip
                          title={
                            isProductJob
                              ? "Internal only - no client emails"
                              : "No retouching required"
                          }
                        >
                          <motion.div
                            whileHover="hover"
                            whileTap="tap"
                            variants={itemVariants}
                          >
                            <ToggleButton
                              value={isProductJob ? "Internal Only" : false}
                              sx={{
                                ...filterButtonStyles,
                                backgroundColor: (
                                  isProductJob
                                    ? clientEmailMode === "Internal Only"
                                    : !retouchingRequired
                                )
                                  ? `${getStatusColor(false)}30`
                                  : "transparent",
                                color: (
                                  isProductJob
                                    ? clientEmailMode === "Internal Only"
                                    : !retouchingRequired
                                )
                                  ? getStatusColor(false)
                                  : isDark
                                  ? "rgba(255,255,255,0.7)"
                                  : "rgba(0,0,0,0.7)",
                                borderColor: (
                                  isProductJob
                                    ? clientEmailMode === "Internal Only"
                                    : !retouchingRequired
                                )
                                  ? `${getStatusColor(false)}80 !important`
                                  : isDark
                                  ? "rgba(255,255,255,0.1) !important"
                                  : "rgba(0,0,0,0.1) !important",
                                "&:hover": {
                                  backgroundColor: (
                                    isProductJob
                                      ? clientEmailMode === "Internal Only"
                                      : !retouchingRequired
                                  )
                                    ? `${getStatusColor(false)}40`
                                    : isDark
                                    ? "rgba(255,255,255,0.05)"
                                    : "rgba(0,0,0,0.05)",
                                },
                              }}
                            >
                              {isProductJob ? "Internal Only" : "No"}
                            </ToggleButton>
                          </motion.div>
                        </Tooltip>
                        <Tooltip
                          title={
                            isProductJob
                              ? "Include client emails"
                              : "Retouching required"
                          }
                        >
                          <motion.div
                            whileHover="hover"
                            whileTap="tap"
                            variants={itemVariants}
                          >
                            <ToggleButton
                              value={isProductJob ? "Include Clients" : true}
                              sx={{
                                ...filterButtonStyles,
                                backgroundColor: (
                                  isProductJob
                                    ? clientEmailMode === "Include Clients"
                                    : retouchingRequired
                                )
                                  ? `${
                                      isProductJob
                                        ? getStatusColor(false)
                                        : getStatusColor(true)
                                    }30`
                                  : "transparent",
                                color: (
                                  isProductJob
                                    ? clientEmailMode === "Include Clients"
                                    : retouchingRequired
                                )
                                  ? isProductJob
                                    ? getStatusColor(false)
                                    : getStatusColor(true)
                                  : isDark
                                  ? "rgba(255,255,255,0.7)"
                                  : "rgba(0,0,0,0.7)",
                                borderColor: (
                                  isProductJob
                                    ? clientEmailMode === "Include Clients"
                                    : retouchingRequired
                                )
                                  ? `${
                                      isProductJob
                                        ? getStatusColor(false)
                                        : getStatusColor(true)
                                    }80 !important`
                                  : isDark
                                  ? "rgba(255,255,255,0.1) !important"
                                  : "rgba(0,0,0,0.1) !important",
                                "&:hover": {
                                  backgroundColor: (
                                    isProductJob
                                      ? clientEmailMode === "Include Clients"
                                      : retouchingRequired
                                  )
                                    ? `${
                                        isProductJob
                                          ? getStatusColor(false)
                                          : getStatusColor(true)
                                      }40`
                                    : isDark
                                    ? "rgba(255,255,255,0.05)"
                                    : "rgba(0,0,0,0.05)",
                                },
                              }}
                            >
                              {isProductJob ? "Include Clients" : "Yes"}
                            </ToggleButton>
                          </motion.div>
                        </Tooltip>
                      </ToggleButtonGroup>
                    </Box>
                  </motion.div>

                  {/* Read-only Payment Terms - Hidden for product jobs */}
                  {!isProductJob && (
                    <motion.div variants={itemVariants}>
                      <Box
                        sx={{
                          flex: { xs: 1, sm: 0 },
                          minWidth: { sm: "150px" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Payment Terms:
                        </Typography>
                        <Chip
                          label={paymentTerms === "cod" ? "COD" : "Supply"}
                          color={paymentTerms === "cod" ? "info" : "default"}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </motion.div>
                  )}

                  {/* Invoice Status - Hidden for product jobs */}
                  {!isProductJob && (
                    <motion.div
                      variants={itemVariants}
                      key={`invoice-status-${jobData?.id}-${
                        invoices.length
                      }-${invoices.map((i) => i.id).join("-")}`}
                    >
                      <Box
                        sx={{
                          flex: { xs: 1, sm: 0 },
                          minWidth: { sm: "150px" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          Invoice Status:
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                          key={`chips-${jobData?.id}-${
                            invoices.length
                          }-${invoiceStatusSummary.join("-")}`}
                        >
                          {invoiceStatusSummary.map((status, index) => (
                            <Chip
                              key={`${jobData?.id}-${status}-${index}-${invoices.length}`}
                              label={status}
                              size="small"
                              sx={{
                                backgroundColor: isDark
                                  ? "rgba(45,45,45,0.7)"
                                  : "rgba(250,250,250,0.9)",
                                color: getInvoiceStatusColor(
                                  status.includes(" ")
                                    ? status.split(" ")[1]
                                    : status
                                ),
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 24,
                                border: `1px solid ${getInvoiceStatusColor(
                                  status.includes(" ")
                                    ? status.split(" ")[1]
                                    : status
                                )}`,
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </motion.div>
                  )}
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>

          {retouchingRequired && (
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
            >
              <Box
                sx={{
                  mb: 1,
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: isDark
                    ? "rgba(35,35,35,0.7)"
                    : "rgba(235,235,235,0.9)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isDark
                    ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                    : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                  padding: 1.5,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Selects Status:
                  </Typography>
                  <ToggleButtonGroup
                    value={selectsStatus}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        setSelectsStatus(newValue);
                      }
                    }}
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <Tooltip title="Selects confirmed">
                      <motion.div
                        whileHover="hover"
                        whileTap="tap"
                        variants={itemVariants}
                      >
                        <ToggleButton
                          value={true}
                          sx={{
                            ...filterButtonStyles,
                            backgroundColor:
                              selectsStatus === true
                                ? `${getStatusColor(false)}30`
                                : "transparent",
                            color:
                              selectsStatus === true
                                ? getStatusColor(false)
                                : isDark
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(0,0,0,0.7)",
                            borderColor:
                              selectsStatus === true
                                ? `${getStatusColor(false)} !important`
                                : isDark
                                ? "rgba(255,255,255,0.1) !important"
                                : "rgba(0,0,0,0.1) !important",
                            "&:hover": {
                              backgroundColor:
                                selectsStatus === true
                                  ? `${getStatusColor(false)}40`
                                  : isDark
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          Confirmed
                        </ToggleButton>
                      </motion.div>
                    </Tooltip>
                    <Tooltip title="Selects not confirmed">
                      <motion.div
                        whileHover="hover"
                        whileTap="tap"
                        variants={itemVariants}
                      >
                        <ToggleButton
                          value={false}
                          sx={{
                            ...filterButtonStyles,
                            backgroundColor:
                              selectsStatus === false
                                ? "rgba(244, 67, 54, 0.3)"
                                : "transparent",
                            color:
                              selectsStatus === false
                                ? "#F44336"
                                : isDark
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(0,0,0,0.7)",
                            borderColor:
                              selectsStatus === false
                                ? "#F44336 !important"
                                : isDark
                                ? "rgba(255,255,255,0.1) !important"
                                : "rgba(0,0,0,0.1) !important",
                            "&:hover": {
                              backgroundColor:
                                selectsStatus === false
                                  ? "rgba(244, 67, 54, 0.4)"
                                  : isDark
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          Not Confirmed
                        </ToggleButton>
                      </motion.div>
                    </Tooltip>
                  </ToggleButtonGroup>

                  {selectsStatus === null && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Please select selects status
                    </Typography>
                  )}
                </Box>
              </Box>
            </motion.div>
          )}

          {
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              <Box
                sx={{
                  mb: 1,
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: isDark
                    ? "rgba(35,35,35,0.7)"
                    : "rgba(235,235,235,0.9)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isDark
                    ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                    : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                  padding: 1.5,
                }}
              >
                {(() => {
                  const getSuggestedList = () => {
                    if (isProductJob) {
                      // Product jobs: based purely on selects status (always retouching)
                      return selectsStatus === true
                        ? "Prep To Send 🎉"
                        : "On Hold 🛑";
                    } else if (!retouchingRequired) {
                      // Life jobs: no retouching logic
                      if (paymentTerms === "cod") {
                        const invoiceStatus = invoiceStatusSummary[0];
                        return invoiceStatus === "Paid"
                          ? "Complete"
                          : "Complete - To Be Invoiced";
                        // : "COD - Waiting for Payment";
                      } else {
                        // Non-COD payment terms
                        const invoiceStatus = invoiceStatusSummary[0];
                        return invoiceStatus === "Paid"
                          ? "Complete"
                          : "Complete - To Be Invoiced";
                      }
                    } else {
                      // Life jobs: retouching required
                      // return selectsStatus === true
                      //   ? "Prep To Send 🎉"
                      //   : "On Hold 🛑";
                      return "On Hold 🛑";
                    }
                  };

                  const suggestedList = getSuggestedList();

                  return (
                    <>
                      <Typography
                        sx={{ mb: 2, display: "block", fontWeight: "bold" }}
                      >
                        Recommended List:{" "}
                        <span
                          style={{ fontWeight: "normal", fontStyle: "italic" }}
                        >
                          {suggestedList}
                        </span>
                      </Typography>
                    </>
                  );
                })()}
                <ListSelector
                  jobData={jobData}
                  idList={idList}
                  setIdList={setIdList}
                />
              </Box>
            </motion.div>
          }

          {/* Images Processed Validation - only for non-product retouching jobs */}
          {!isProductJob &&
            retouchingRequired &&
            selectsStatus !== null &&
            !imagesProcessedComplete && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <Box
                  sx={{
                    mb: 1,
                    p: 2,
                    borderRadius: "10px",
                    backgroundColor: isDark
                      ? "rgba(244, 67, 54, 0.1)"
                      : "rgba(244, 67, 54, 0.05)",
                    border: `1px solid ${
                      isDark
                        ? "rgba(244, 67, 54, 0.3)"
                        : "rgba(244, 67, 54, 0.2)"
                    }`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      color: "#F44336",
                      display: "flex",
                      alignItems: "center",
                      minWidth: "24px",
                    }}
                  >
                    ⚠️
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#F44336",
                        mb: 0.5,
                      }}
                    >
                      {imagesProcessedText}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark
                          ? "rgba(244, 67, 54, 0.8)"
                          : "rgba(244, 67, 54, 0.7)",
                      }}
                    >
                      Please fill in the images processed number in the Invoice
                      Data tab before sending the email.
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            )}

          {/* Operator Checklist - only for retouching jobs with selected status, not for product jobs */}
          {!isProductJob && retouchingRequired && selectsStatus !== null && (
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
            >
              <Box sx={{ mb: 2 }}>
                <StoryboardOperatorChecklist
                  selectsStatus={selectsStatus}
                  jobData={jobData}
                  clientData={clientData}
                  onComplete={setChecklistComplete}
                  disabled={loading}
                />
              </Box>
            </motion.div>
          )}

          {/* Error message */}
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} disabled={loading} color="inherit">
            Cancel
          </Button>

          <ButtonGroup
            variant="contained"
            sx={{
              "& .MuiButton-root": { borderRadius: 0 },
              "& .MuiButton-root:first-of-type": {
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
              },
              "& .MuiButton-root:last-of-type": {
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
              },
            }}
          >
            <Button
              onClick={() => handleContinue()}
              disabled={!canProceed() || loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? "Loading..." : "Send Email"}
            </Button>
            <Button
              onClick={(event) =>
                setTemplateOverrideMenuAnchor(event.currentTarget)
              }
              disabled={loading}
              sx={{
                px: 0.5,
                minWidth: "28px",
                maxWidth: "28px",
                borderLeft: `1px solid ${
                  isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
                }`,
                "&:hover": {
                  borderLeft: `1px solid ${
                    isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"
                  }`,
                },
              }}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>
        </DialogActions>
      </Dialog>

      {/* Email Modal */}
      <EmailModalV2
        open={emailModalOpen}
        onClose={handleEmailModalClose}
        initialTo={emailToRecipients}
        initialCc={emailCcRecipients}
        initialBcc={[]}
        template={emailTemplate}
        subject={emailSubject}
        fromEmail="production@imagesthatsell.com.au"
        onSubjectChange={handleSubjectChange}
        onSend={handleSend}
      />

      {/* Template Override Menu */}
      <Menu
        anchorEl={templateOverrideMenuAnchor}
        open={Boolean(templateOverrideMenuAnchor)}
        onClose={() => setTemplateOverrideMenuAnchor(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: isDark ? "#2c2c2e" : "#ffffff",
              color: isDark ? "#ffffff" : "#000000",
              border: `1px solid ${isDark ? "#3c3c3e" : "#e0e0e0"}`,
              mt: -1,
              minWidth: 280,
            },
          },
        }}
      >
        <MenuItem disabled sx={{ py: 1, px: 2 }}></MenuItem>
        {emailTemplateOptions.map((template) => (
          <MenuItem
            key={template.configKey}
            onClick={() => handleTemplateOverride(template.configKey)}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          >
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {template.displayName}
                </Typography>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {template.description}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default StoryboardDetailsModal;
